import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Forward, X, Loader2, Mail, Search, Paperclip, CheckCircle2, Languages } from 'lucide-react';

const DEFAULT_MESSAGES = {
  fr:
    "Bonjour,\n\nNous avons une nouvelle demande de prix qui pourrait correspondre à vos capacités. " +
    "Vous trouverez le détail ci-dessous (plans et fichiers 3D en pièces jointes).\n\n" +
    "Rendez-vous sur prodpilots.com pour recevoir plus de demandes de prix et gérer vos devis en ligne.\n\n" +
    "À très vite,\nL'équipe ProdLink",
  en:
    "Hello,\n\nWe have a new price request that may match your capabilities. " +
    "You will find the details below (drawings and 3D files attached).\n\n" +
    "Visit prodpilots.com to receive more targeted requests and manage your quotes online.\n\n" +
    "Best regards,\nThe ProdLink team",
};

export default function ForwardPRDialog({ pr, onClose }) {
  const [subs, setSubs] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedSubs, setSelectedSubs] = useState(new Set());
  const [customEmails, setCustomEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [language, setLanguage] = useState('fr');
  const [message, setMessage] = useState(DEFAULT_MESSAGES.fr);
  const [messageEdited, setMessageEdited] = useState(false);
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!pr) return;
    api.get('/subcontractors').then(({ data }) => {
      // Keep only subs with a valid email
      setSubs((data || []).filter(s => s.email && s.email.includes('@')));
    }).catch(() => setSubs([]));
  }, [pr]);

  // When language changes, swap the default message unless the user has manually edited it
  useEffect(() => {
    if (!messageEdited) {
      setMessage(DEFAULT_MESSAGES[language] || DEFAULT_MESSAGES.fr);
    }
  }, [language, messageEdited]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return subs;
    return subs.filter(s =>
      (s.company_name || '').toLowerCase().includes(q)
      || (s.contact_name || '').toLowerCase().includes(q)
      || (s.email || '').toLowerCase().includes(q)
      || (s.specialty || '').toLowerCase().includes(q)
      || (s.country || '').toLowerCase().includes(q)
      || (s.specialties || []).some(sp => (sp || '').toLowerCase().includes(q))
    );
  }, [subs, search]);

  const toggleSub = (id) => {
    setSelectedSubs(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const addCustomEmail = () => {
    const v = emailInput.trim().toLowerCase();
    if (!v || !v.includes('@')) { toast.error('Email invalide'); return; }
    if (customEmails.includes(v)) { setEmailInput(''); return; }
    setCustomEmails(prev => [...prev, v]);
    setEmailInput('');
  };

  const totalRecipients = selectedSubs.size + customEmails.length;
  const attachCount = (pr?.attachments_meta || []).length;

  const submit = async () => {
    if (!pr) return;
    if (totalRecipients === 0) { toast.error('Sélectionne au moins un destinataire'); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/price-requests/${pr.id}/forward-by-email`, {
        subcontractor_ids: Array.from(selectedSubs),
        custom_emails: customEmails,
        custom_message: message,
        include_attachments: includeAttachments,
        language,
      });
      const sentMsg = `${data.sent}/${data.sent + data.failed} envoyé(s)`;
      const attMsg = data.attachments_attached
        ? ` · ${data.attachments_attached} pièce(s) jointe(s)` : '';
      if (data.failed > 0) {
        toast.warning(`${sentMsg}${attMsg} (${data.failed} échec${data.failed > 1 ? 's' : ''})`);
      } else {
        toast.success(`${sentMsg}${attMsg}`);
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur envoi');
    } finally { setSending(false); }
  };

  if (!pr) return null;

  return (
    <Dialog open={!!pr} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="forward-pr-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Forward className="w-5 h-5 text-blue-400" />
            Transférer la demande par email
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            <span className="font-medium text-zinc-300">{pr.client_name}</span> · {pr.description?.slice(0, 80)}{pr.description?.length > 80 ? '…' : ''}
            {attachCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-blue-400 text-xs">
                <Paperclip className="w-3 h-3" /> {attachCount} fichier(s) attachable(s)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-3">
          {/* Subcontractor multi-select */}
          <section>
            <div className="flex items-baseline justify-between mb-2">
              <Label className="text-sm font-medium text-zinc-200">Sous-traitants enregistrés</Label>
              <span className="text-[11px] text-zinc-500">{selectedSubs.size} sélectionné(s) · {filtered.length}/{subs.length}</span>
            </div>
            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-500" />
              <Input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher (nom, email, spécialité, pays)…"
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm pl-8 h-9 text-sm"
                data-testid="fwd-sub-search"
              />
            </div>
            <div className="max-h-[200px] overflow-y-auto bg-zinc-950 border border-zinc-800 rounded-sm divide-y divide-zinc-800/60">
              {filtered.length === 0 ? (
                <div className="text-xs text-zinc-500 italic px-3 py-3 text-center">Aucun sous-traitant trouvé</div>
              ) : filtered.map(s => {
                const checked = selectedSubs.has(s.id);
                return (
                  <button key={s.id} type="button" onClick={() => toggleSub(s.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${checked ? 'bg-blue-500/10' : 'hover:bg-zinc-900'}`}
                    data-testid={`fwd-sub-${s.id}`}>
                    <div className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${checked ? 'bg-blue-500 border-blue-500' : 'border-zinc-700'}`}>
                      {checked && <CheckCircle2 className="w-3 h-3 text-zinc-950" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-100 truncate">{s.company_name}</div>
                      <div className="text-[11px] text-zinc-500 truncate">{s.email} · {s.country || '—'} · {s.specialty || (s.specialties || []).join(', ') || '—'}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Free emails */}
          <section>
            <Label className="text-sm font-medium text-zinc-200 mb-2 block">Emails libres</Label>
            <div className="flex gap-2 mb-2">
              <div className="relative flex-1">
                <Mail className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-500" />
                <Input
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomEmail(); } }}
                  placeholder="contact@atelier.fr"
                  type="email"
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm pl-8 h-9 text-sm"
                  data-testid="fwd-custom-email"
                />
              </div>
              <Button type="button" onClick={addCustomEmail} size="sm" variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm h-9" data-testid="fwd-add-email">
                Ajouter
              </Button>
            </div>
            {customEmails.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {customEmails.map(e => (
                  <span key={e} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-blue-500/10 border border-blue-500/30 text-blue-300">
                    {e}
                    <button onClick={() => setCustomEmails(prev => prev.filter(x => x !== e))} className="hover:text-rose-400" data-testid={`fwd-remove-${e}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* Language toggle + Message */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium text-zinc-200">Message d'accompagnement</Label>
              <div className="inline-flex items-center gap-1 bg-zinc-950 border border-zinc-800 rounded-sm p-0.5" data-testid="fwd-lang-toggle">
                <Languages className="w-3.5 h-3.5 text-zinc-500 ml-1.5" />
                <button
                  type="button"
                  onClick={() => setLanguage('fr')}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded-sm transition-colors ${language === 'fr' ? 'bg-blue-500/20 text-blue-300' : 'text-zinc-500 hover:text-zinc-300'}`}
                  data-testid="fwd-lang-fr"
                >
                  FR
                </button>
                <button
                  type="button"
                  onClick={() => setLanguage('en')}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded-sm transition-colors ${language === 'en' ? 'bg-blue-500/20 text-blue-300' : 'text-zinc-500 hover:text-zinc-300'}`}
                  data-testid="fwd-lang-en"
                >
                  EN
                </button>
              </div>
            </div>
            <Textarea
              value={message}
              onChange={(e) => { setMessage(e.target.value); setMessageEdited(true); }}
              className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm min-h-[140px] text-sm"
              placeholder={language === 'en' ? 'Personal note for the recipient…' : "Mot personnalisé pour le destinataire…"}
              data-testid="fwd-message"
            />
            <p className="text-[11px] text-zinc-600 mt-1">
              {language === 'en'
                ? 'Appears at the top of the email. The full request body and CTA will be translated to English.'
                : "Apparaît en haut de l'email, avant la fiche technique de la demande."}
            </p>
          </section>

          {/* Attachments toggle */}
          {attachCount > 0 && (
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={includeAttachments}
                onChange={(e) => setIncludeAttachments(e.target.checked)}
                className="accent-blue-500"
                data-testid="fwd-include-attachments"
              />
              Joindre les {attachCount} fichier(s) (PDFs, plans, 3D) — max 18 MB total
            </label>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">
            Annuler
          </Button>
          <Button onClick={submit} disabled={sending || totalRecipients === 0}
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-medium" data-testid="fwd-submit">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Forward className="w-4 h-4 mr-2" />}
            Envoyer à {totalRecipients} destinataire{totalRecipients > 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
